import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { LucideIcon } from 'lucide-react';
import { Button } from '@/components/ui';
import { NotLegalIcon } from '@/components/ui/NotLegalIcon';
import { MessageSquare, FileText, Zap, Shield, Coins, Play, Check, AlertCircle, Gavel, Scale, Building2, FileSignature, Hammer } from 'lucide-react';
import { cn } from '@/lib/utils';

gsap.registerPlugin(ScrollTrigger);

// ============================================
// ASCII ART LEGAL ELEMENTS
// ============================================
const ASCII_SCALES = `
        \\   |   /
         \\  |  /
          \\ | /
       ____\\|/____
      |            |
      |  JUSTICE   |
      |     ||     |
      |   ==  ==   |
      |            |
    __|____________|__
   /                 \\
  /                   \\
 /                     \\
`;

const ASCII_GAVEL = `
        __||__
       /    \\
      /      \\
     /  GAVEL \\
    /_________\\
        ||
        ||
        ||
       /\\
      /  \\
     /    \\
    /      \\
`;

const ASCII_COURTHOUSE = `
      /\\
     /  \\
    / ** \\
   /      \\
  /   __   \\
 /   /  \\   \\
|    |  |    |
|    |  |    |
|    |  |    |
|    |__|    |
|    __  |   |
|   |  | |   |
|   |__|_|   |
|___________|
`;

const ASCII_DOCUMENT = `
 _____________________
|  LEGAL DOCUMENT    |
|___________________|
|                   |
|  I agree to all   |
|  the things and   |
|  also some other  |
|  stuff. Sign here:|
|                   |
|  _________________|
|  |               ||
|  |_______________||
|___________________|
`;

// ============================================
// SCENE LAYER - PARALLAX ASCII ART
// ============================================
interface SceneLayerProps {
  ascii: string;
  className?: string;
  speed?: number;
  opacity?: number;
  color?: string;
}

function SceneLayer({ ascii, className, speed = 1, opacity = 0.3, color = 'primary' }: SceneLayerProps) {
  const layerRef = useRef<HTMLPreElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useGSAP(() => {
    if (!layerRef.current || !containerRef.current) return;

    gsap.to(layerRef.current, {
      y: speed * 100,
      x: speed * 30,
      rotation: speed * 5,
      scrollTrigger: {
        trigger: containerRef.current,
        start: 'top bottom',
        end: 'bottom top',
        scrub: true,
      },
    });
  }, { scope: containerRef });

  const colorClass = {
    primary: 'text-primary-400',
    secondary: 'text-secondary-400',
    warning: 'text-warning-400',
    success: 'text-green-400',
  }[color];

  return (
    <div ref={containerRef} className={cn('absolute pointer-events-none select-none', className)}>
      <pre
        ref={layerRef}
        className={cn('text-xs md:text-sm whitespace-pre leading-none font-mono', colorClass)}
        style={{ opacity, fontSize: 'clamp(6px, 1.5vw, 12px)' }}
      >
        {ascii}
      </pre>
    </div>
  );
}

// ============================================
// ZOOM THROUGH HERO
// ============================================
function ZoomThroughHero() {
  const containerRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLDivElement>(null);
  const layerRefs = useRef<(HTMLDivElement | null)[]>([]);

  useGSAP(() => {
    if (!textRef.current) return;

    const letters = textRef.current.querySelectorAll('.zoom-letter');
    const layers = layerRefs.current.filter(Boolean);

    // Camera zoom through effect
    const tl = gsap.timeline();

    // Initial state - all letters pushed back
    tl.set(letters, {
      opacity: 0,
      scale: 0.1,
      z: -500,
      filter: 'blur(10px)',
    });

    // Camera flies through each letter
    letters.forEach((letter, i) => {
      tl.to(letter, {
        opacity: 1,
        scale: 1,
        z: 0,
        filter: 'blur(0px)',
        duration: 0.1,
        ease: 'power4.out',
      }, i * 0.08);

      // Letter zooms past camera (moves to front)
      tl.to(letter, {
        opacity: 0,
        scale: 15,
        z: 500,
        filter: 'blur(5px)',
        duration: 0.15,
        ease: 'power4.in',
      }, i * 0.08 + 0.05);
    });

    // Parallax layers move
    layers.forEach((layer, i) => {
      if (!layer) return;
      gsap.to(layer, {
        y: -100 * (i + 1),
        x: 50 * (i % 2 === 0 ? 1 : -1),
        scrollTrigger: {
          trigger: containerRef.current,
          start: 'top top',
          end: 'bottom top',
          scrub: 1,
        },
      });
    });

  }, { scope: containerRef });

  const headline = 'NOT LEGAL ADVICE';
  const letters = headline.split('');

  return (
    <div ref={containerRef} className="relative h-screen overflow-hidden flex items-center justify-center">
      {/* Parallax ASCII layers */}
      <SceneLayer ascii={ASCII_COURTHOUSE} className="top-[10%] left-[5%]" speed={-0.5} opacity={0.15} color="primary" />
      <SceneLayer ascii={ASCII_GAVEL} className="top-[20%] right-[8%]" speed={0.3} opacity={0.2} color="secondary" />
      <SceneLayer ascii={ASCII_SCALES} className="bottom-[15%] left-[10%]" speed={-0.3} opacity={0.2} color="warning" />
      <SceneLayer ascii={ASCII_DOCUMENT} className="bottom-[25%] right-[5%]" speed={0.4} opacity={0.15} color="success" />

      {/* Camera zoom text */}
      <div ref={textRef} className="relative z-10 perspective-1000">
        {letters.map((letter, i) => (
          <span
            key={i}
            className="zoom-letter inline-block text-6xl md:text-9xl font-black text-surface-900 mx-1 md:mx-2"
            style={{ transformStyle: 'preserve-3d', perspective: '1000px' }}
          >
            {letter}
          </span>
        ))}
      </div>

      {/* Subtext with fade in */}
      <div className="absolute bottom-[20%] left-0 right-0 text-center z-20">
        <p className="text-xl md:text-2xl text-surface-600 px-4">
          But it will sure feel like it
        </p>
      </div>
    </div>
  );
}

// ============================================
// TEXT SWIPE COMPONENT
// ============================================
interface TextSwipeProps {
  text: string;
  className?: string;
  direction?: 'left' | 'right' | 'up' | 'down';
  delay?: number;
}

function TextSwipe({ text, className, direction = 'up', delay = 0 }: TextSwipeProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const words = text.split(' ');

  useGSAP(() => {
    if (!containerRef.current) return;

    const wordElements = containerRef.current.querySelectorAll('.swipe-word');

    gsap.fromTo(wordElements,
      getInitialTransform(direction),
      {
        opacity: 1,
        x: 0,
        y: 0,
        duration: 0.8,
        stagger: 0.1,
        ease: 'power4.out',
        delay,
        scrollTrigger: {
          trigger: containerRef.current,
          start: 'top 85%',
          toggleActions: 'play none none reverse',
        },
      }
    );
  }, { scope: containerRef });

  const getInitialTransform = (dir: string) => {
    switch (dir) {
      case 'left': return { x: -100, opacity: 0 };
      case 'right': return { x: 100, opacity: 0 };
      case 'up': return { y: 50, opacity: 0 };
      case 'down': return { y: -50, opacity: 0 };
      default: return { y: 50, opacity: 0 };
    }
  };

  return (
    <div ref={containerRef} className={className}>
      {words.map((word, i) => (
        <span
          key={i}
          className="swipe-word inline-block mr-2 md:mr-3"
          style={{ whiteSpace: 'nowrap' }}
        >
          {word}
        </span>
      ))}
    </div>
  );
}

// ============================================
// GAVEL TRANSITION EFFECT
// ============================================
interface GavelTransitionProps {
  onSlam?: () => void;
}

function GavelTransition({ onSlam }: GavelTransitionProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const gavelRef = useRef<HTMLDivElement>(null);
  const slamRef = useRef<HTMLDivElement>(null);
  const particlesRef = useRef<HTMLDivElement[]>([]);

  useGSAP(() => {
    if (!containerRef.current || !gavelRef.current || !slamRef.current) return;

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: containerRef.current,
        start: 'top 70%',
        onEnter: () => tl.restart(),
        onLeaveBack: () => tl.pause(),
      },
    });

    // Gavel swings down
    tl.set(gavelRef.current, { rotation: -45, x: -100, opacity: 0 });
    tl.set(slamRef.current, { scale: 0, opacity: 0 });
    tl.set('.slam-particle', { scale: 0, opacity: 1 });

    tl.to(gavelRef.current, {
      rotation: 15,
      x: 0,
      opacity: 1,
      duration: 0.4,
      ease: 'power2.out',
    });

    // SLAM!
    tl.to(gavelRef.current, {
      rotation: 30,
      duration: 0.05,
      ease: 'power4.in',
      onComplete: () => onSlam?.(),
    });

    // Impact waves
    tl.to(slamRef.current, {
      scale: 1,
      opacity: 1,
      duration: 0.1,
      ease: 'power4.out',
    }, '-=0.05');

    tl.to(slamRef.current, {
      scale: 2,
      opacity: 0,
      duration: 0.4,
      ease: 'power2.out',
    });

    // Particles explode
    tl.to('.slam-particle', {
      scale: 1,
      x: (i) => (i % 4 - 1.5) * 100,
      y: (i) => Math.floor(i / 4) * -80 - 40,
      rotation: Math.random() * 720 - 360,
      duration: 0.6,
      stagger: 0.02,
      ease: 'power4.out',
    }, '-=0.3');

    tl.to('.slam-particle', {
      opacity: 0,
      duration: 0.3,
    }, '-=0.2');

  }, { scope: containerRef });

  return (
    <div ref={containerRef} className="relative py-8 flex justify-center items-center">
      {/* Impact wave */}
      <div
        ref={slamRef}
        className="absolute w-32 h-32 rounded-full border-4 border-primary-500 pointer-events-none"
        style={{ transformOrigin: 'center' }}
      />

      {/* Gavel icon */}
      <div
        ref={gavelRef}
        className="relative z-10"
        style={{ transformOrigin: 'left center' }}
      >
        <Gavel className="w-20 h-20 text-amber-700 drop-shadow-2xl" />
      </div>

      {/* Particles */}
      {[...Array(12)].map((_, i) => (
        <div
          key={i}
          className="slam-particle absolute w-2 h-2 bg-primary-500 rounded-full"
          style={{ left: '50%', top: '50%' }}
        />
      ))}
    </div>
  );
}

// ============================================
// SCROLL-CONTROLLED GAVEL HAMMER
// ============================================
interface ScrollGavelProps {
  sections: Array<{ id: string; label: string }>;
}

function ScrollGavel({ sections }: ScrollGavelProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const gavelRef = useRef<HTMLDivElement>(null);
  const headRef = useRef<HTMLDivElement>(null);
  const handleRef = useRef<HTMLDivElement>(null);
  const impactRef = useRef<HTMLDivElement>(null);

  useGSAP(() => {
    if (!gavelRef.current || !headRef.current || !handleRef.current || !impactRef.current) return;

    const gavel = gavelRef.current;
    const head = headRef.current;
    const handle = handleRef.current;
    const impact = impactRef.current;

    // Set transform origin for rotation
    head.style.transformOrigin = 'left center';
    handle.style.transformOrigin = 'center 20%';

    let lastScrollY = 0;
    let isSlamming = false;

    // Scroll controls gavel rotation
    ScrollTrigger.create({
      trigger: document.body,
      start: 'top top',
      end: 'bottom bottom',
      onUpdate: (self) => {
        if (isSlamming) return;

        const scrollY = self.scroll();
        const deltaY = scrollY - lastScrollY;
        lastScrollY = scrollY;

        // Calculate rotation based on scroll
        // Start at 0° (upright), rotate to ~90° as user scrolls
        const progress = Math.min(scrollY / window.innerHeight, 1.5);
        const rotation = Math.min(progress * 90, 110);

        // Add some lag for weight feel
        gsap.to(gavel, {
          rotation: rotation,
          duration: 0.3,
          ease: 'power2.out',
        });

        // Handle moves with gavel head
        gsap.to(handle, {
          rotation: rotation * 0.5,
          duration: 0.3,
        });

        // Quick scroll = faster rotation = potential slam
        if (Math.abs(deltaY) > 15 && !isSlamming) {
          // Fast scroll - trigger slam at section boundaries
          triggerSlam();
        }
      },
    });

    function triggerSlam() {
      if (isSlamming) return;
      isSlamming = true;

      // Fast downward slam animation
      const tl = gsap.timeline({
        onComplete: () => {
          isSlamming = false;
        },
      });

      // Wind up (slight backward movement)
      tl.to(gavel, {
        rotation: -15,
        duration: 0.1,
        ease: 'power2.in',
      });

      // SLAM down!
      tl.to(gavel, {
        rotation: 95,
        duration: 0.05,
        ease: 'power4.in',
      });

      // Impact!
      tl.to(impact, {
        scale: 1,
        opacity: 1,
        duration: 0.05,
        ease: 'power4.out',
      });

      tl.to(impact, {
        scale: 2,
        opacity: 0,
        duration: 0.3,
        ease: 'power2.out',
      });

      // Screen shake
      tl.to('body', {
        x: -5,
        duration: 0.02,
        repeat: 5,
        yoyo: true,
        ease: 'power2.inOut',
      }, '-=0.05');

      // Reset gavel position
      tl.to(gavel, {
        rotation: 0,
        duration: 0.5,
        ease: 'elastic.out(1, 0.5)',
        delay: 0.2,
      });
    }

    // Click to slam
    window.addEventListener('click', triggerSlam);
    window.addEventListener('keydown', (e) => {
      if (e.code === 'Space') {
        e.preventDefault();
        triggerSlam();
      }
    });

    return () => {
      window.removeEventListener('click', triggerSlam);
    };
  }, { scope: containerRef });

  return (
    <div ref={containerRef} className="fixed top-0 left-0 right-0 z-[100] pointer-events-none">
      {/* Gavel - fixed position, scroll-controlled */}
      <div
        ref={gavelRef}
        className="absolute top-20 right-8 md:right-16"
        style={{ transformOrigin: 'top center' }}
      >
        {/* Gavel Head */}
        <div
          ref={headRef}
          className="flex items-center"
          style={{ transformOrigin: 'left center' }}
        >
          {/* Main hammer head */}
          <div className="relative">
            <div className="w-8 h-16 bg-amber-800 rounded-lg shadow-2xl flex items-center justify-center">
              <div className="w-6 h-12 bg-amber-700 rounded border-2 border-amber-600" />
            </div>
            {/* Metal bands */}
            <div className="absolute top-2 left-0 w-full h-1 bg-amber-500" />
            <div className="absolute bottom-2 left-0 w-full h-1 bg-amber-500" />
          </div>
        </div>

        {/* Handle */}
        <div
          ref={handleRef}
          className="w-4 h-32 bg-amber-600 rounded-full mx-auto -mt-1 shadow-xl"
          style={{
            clipPath: 'polygon(20% 0%, 80% 0%, 100% 100%, 0% 100%)',
          }}
        >
          {/* Grip */}
          <div className="w-full h-16 bg-amber-500 rounded-b-full" />
        </div>

        {/* Impact wave */}
        <div
          ref={impactRef}
          className="absolute top-1/2 left-0 w-24 h-24 -translate-y-1/2 -translate-x-12 rounded-full border-4 border-red-500 opacity-0 pointer-events-none"
          style={{ transformOrigin: 'center' }}
        />
      </div>

      {/* Section indicators */}
      <div className="absolute top-20 left-4 flex flex-col gap-2 pointer-events-none">
        {sections.map((section) => (
          <div
            key={section.id}
            className="text-xs text-surface-400 font-mono opacity-50"
          >
            {section.label}
          </div>
        ))}
      </div>

      {/* Instructions */}
      <div className="absolute bottom-4 right-4 text-xs text-surface-400 font-mono pointer-events-none">
        Scroll to swing • Click to slam
      </div>
    </div>
  );
}

// ============================================
// DOCUMENT UNROLL PRICING
// ============================================
interface PricingTier {
  name: string;
  price: string;
  tokens: string;
  features: string[];
  popular?: boolean;
}

function DocumentUnroll({ tiers }: { tiers: PricingTier[] }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const paperRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<(HTMLDivElement | null)[]>([]);
  const sealRef = useRef<HTMLDivElement>(null);

  useGSAP(() => {
    if (!paperRef.current) return;

    // Paper unroll animation
    gsap.set(paperRef.current, {
      clipPath: 'polygon(0 0, 100% 0, 100% 0, 0 0)',
    });

    gsap.to(paperRef.current, {
      clipPath: 'polygon(0 0, 100% 0, 100% 100%, 0 100%)',
      duration: 1.2,
      ease: 'power3.inOut',
      scrollTrigger: {
        trigger: containerRef.current,
        start: 'top 75%',
      },
    });

    // Cards slide out
    cardsRef.current.forEach((card, i) => {
      if (!card) return;
      gsap.set(card, { y: 100, opacity: 0 });

      gsap.to(card, {
        y: 0,
        opacity: 1,
        duration: 0.6,
        delay: 0.8 + i * 0.2,
        ease: 'back.out(1.7)',
        scrollTrigger: {
          trigger: containerRef.current,
          start: 'top 75%',
        },
      });
    });

    // Official seal slams down
    if (sealRef.current) {
      gsap.set(sealRef.current, { scale: 3, y: -200, opacity: 0 });

      gsap.to(sealRef.current, {
        scale: 1,
        y: 0,
        opacity: 1,
        duration: 0.4,
        ease: 'elastic.out(1, 0.5)',
        delay: 1.5,
        scrollTrigger: {
          trigger: containerRef.current,
          start: 'top 75%',
        },
      });
    }

  }, { scope: containerRef });

  return (
    <div ref={containerRef} className="relative">
      {/* Document paper */}
      <div
        ref={paperRef}
        className="bg-amber-50 rounded-lg shadow-2xl p-8 relative overflow-hidden"
        style={{
          backgroundImage: 'linear-gradient(90deg, transparent 79px, #d4a574 79px, #d4a574 81px, transparent 81px)',
          backgroundSize: '100% 100%',
        }}
      >
        {/* Document header */}
        <div className="text-center mb-8 relative">
          <h3 className="text-2xl font-bold text-amber-900 font-serif border-b-2 border-amber-300 pb-2 inline-block">
            CONTRACT OF SERVICES
          </h3>
          <p className="text-amber-700 text-sm mt-2">Not legally binding but we tried</p>
        </div>

        {/* Pricing cards emerge from document */}
        <div className="grid md:grid-cols-3 gap-6 relative z-10">
          {tiers.map((tier, i) => (
            <div
              key={i}
              ref={(el) => { cardsRef.current[i] = el; }}
              className={cn(
                'bg-white rounded-lg p-6 shadow-lg border-2 relative',
                tier.popular ? 'border-primary-500 ring-2 ring-primary-500/30' : 'border-amber-200'
              )}
            >
              {tier.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary-600 text-white text-xs font-bold px-4 py-1 rounded-full">
                  MOST POPULAR
                </div>
              )}

              <h4 className="text-xl font-bold text-surface-900">{tier.name}</h4>
              <div className="mt-4 flex items-baseline">
                <span className="text-4xl font-black text-surface-900">{tier.price}</span>
                <span className="text-surface-500 ml-1">/mo</span>
              </div>
              <p className="text-primary-600 font-semibold mt-1">{tier.tokens}</p>

              <ul className="mt-6 space-y-3">
                {tier.features.map((feature, j) => (
                  <li key={j} className="flex items-center text-sm text-surface-600">
                    <Check className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>

              <Link to="/sign-up" className="block mt-6">
                <Button
                  variant={tier.popular ? 'primary' : 'secondary'}
                  className="w-full"
                >
                  SIGN HERE
                </Button>
              </Link>
            </div>
          ))}
        </div>

        {/* Official seal */}
        <div
          ref={sealRef}
          className="absolute bottom-4 right-4 w-16 h-16 rounded-full bg-red-600 flex items-center justify-center shadow-lg z-20"
        >
          <div className="text-center">
            <div className="text-[10px] text-white font-bold leading-tight">NOT</div>
            <div className="text-[10px] text-white font-bold leading-tight">LEGAL</div>
            <div className="w-8 h-0.5 bg-white mx-auto my-0.5" />
            <div className="text-[8px] text-white font-bold leading-tight">CHAT</div>
          </div>
        </div>

        {/* Paper texture overlay */}
        <div className="absolute inset-0 pointer-events-none opacity-5">
          <div className="absolute inset-0 bg-[radial-gradient(circle,_transparent_20%,_currentColor_200%)]" />
        </div>
      </div>
    </div>
  );
}

// ============================================
// SECTION WRAPPER WITH IMPACT
// ============================================
interface SectionWrapperProps {
  children: React.ReactNode;
  className?: string;
  dark?: boolean;
}

function SectionWrapper({ children, className, dark = false }: SectionWrapperProps) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useGSAP(() => {
    if (!wrapperRef.current || !contentRef.current) return;

    // Screen shake on enter
    gsap.set(contentRef.current, { x: 0 });

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: wrapperRef.current,
        start: 'top 80%',
        onEnter: () => {
          gsap.fromTo(contentRef.current,
            { keyframes: { "0%": { x: 0 }, "20%": { x: -10 }, "40%": { x: 8 }, "60%": { x: -6 }, "80%": { x: 4 }, "100%": { x: 0 } } },
            { x: 0, duration: 0.5, ease: 'power2.out' }
          );
        },
      },
    });
  }, { scope: wrapperRef });

  return (
    <section
      ref={wrapperRef}
      className={cn(
        'py-24 px-6 relative overflow-hidden',
        dark ? 'bg-surface-900' : 'bg-white',
        className
      )}
    >
      {/* Impact vignette on scroll */}
      <div className="absolute inset-0 pointer-events-none bg-gradient-to-b from-transparent to-black/5" />

      <div ref={contentRef} className="relative z-10">
        {children}
      </div>
    </section>
  );
}

// ============================================
// FEATURE CARD WITH SHATTER EFFECT
// ============================================
interface FeatureCardProps {
  title: string;
  description: string;
  icon: LucideIcon;
  className?: string;
}

function FeatureCard({ title, description, icon: Icon, className }: FeatureCardProps) {
  const cardRef = useRef<HTMLDivElement>(null);

  useGSAP(() => {
    if (!cardRef.current) return;

    gsap.fromTo(cardRef.current,
      {
        scale: 0.9,
        opacity: 0,
        rotationX: 30,
      },
      {
        scale: 1,
        opacity: 1,
        rotationX: 0,
        duration: 0.8,
        ease: 'power4.out',
        scrollTrigger: {
          trigger: cardRef.current,
          start: 'top 85%',
          toggleActions: 'play none none reverse',
        },
      }
    );
  }, { scope: cardRef });

  return (
    <div
      ref={cardRef}
      className={cn(
        'bg-surface-50 rounded-2xl p-8 border border-surface-200',
        'hover:shadow-2xl hover:border-primary-300 transition-all duration-300',
        'transform-gpu',
        className
      )}
    >
      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary-100 to-primary-200 flex items-center justify-center mb-6">
        <Icon className="w-8 h-8 text-primary-600" />
      </div>

      <h3 className="text-2xl font-bold text-surface-900 mb-3">{title}</h3>
      <p className="text-surface-600 leading-relaxed">{description}</p>
    </div>
  );
}

// ============================================
// TYPEWRITER HEADLINE WITH STYLE
// ============================================
function TypewriterHeadline() {
  const containerRef = useRef<HTMLDivElement>(null);
  const cursorRef = useRef<HTMLSpanElement>(null);

  const headlines = [
    { text: 'Legal insights', subtext: '*minus the billable hours*' },
    { text: 'AI answers', subtext: '*that come with disclaimers*' },
    { text: 'Smart legal-ish info', subtext: '*no suit required*' },
    { text: 'Understanding fine print', subtext: '*before it bites you*' },
    { text: 'Contract decoder', subtext: '*without selling your soul*' },
  ];

  const [index, setIndex] = useState(0);
  const [displayText, setDisplayText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [showSubtext, setShowSubtext] = useState(false);

  useEffect(() => {
    const current = headlines[index];
    const fullText = current.text;
    const speed = isDeleting ? 30 : 60;

    const timer = setTimeout(() => {
      if (isDeleting) {
        setDisplayText(prev => prev.slice(0, -1));
        if (displayText === '') {
          setIsDeleting(false);
          setShowSubtext(false);
          setIndex(prev => (prev + 1) % headlines.length);
        }
      } else {
        setDisplayText(fullText.slice(0, displayText.length + 1));
        if (displayText === fullText) {
          setShowSubtext(true);
          setTimeout(() => setIsDeleting(true), 2500);
        }
      }
    }, speed);

    return () => clearTimeout(timer);
  }, [displayText, isDeleting, index]);

  // Cursor blink
  useEffect(() => {
    const cursor = cursorRef.current;
    if (!cursor) return;

    const interval = setInterval(() => {
      cursor.style.opacity = cursor.style.opacity === '0' ? '1' : '0';
    }, 500);

    return () => clearInterval(interval);
  }, []);

  return (
    <div ref={containerRef} className="text-center">
      <h1 className="text-4xl md:text-6xl font-black text-surface-900 tracking-tight">
        {displayText}
        <span
          ref={cursorRef}
          className="inline-block w-1 h-10 bg-primary-500 ml-2 align-middle"
        />
      </h1>
      {showSubtext && (
        <p className="text-xl md:text-2xl text-primary-600 mt-4 font-medium animate-in fade-in slide-in-from-top-2">
          {headlines[index].subtext}
        </p>
      )}
    </div>
  );
}

// ============================================
// MAIN LANDING PAGE
// ============================================
export function LandingPage() {
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement>(null);
  const ctaButtonRef = useRef<HTMLButtonElement>(null);

  const handleDemoClick = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate('/chat?demo=true');
  };

  const features = [
    {
      title: 'Cheeky Legal Chat',
      description: 'Get answers with the personality of a witty attorney who has had too much coffee.',
      icon: MessageSquare,
    },
    {
      title: 'Document Analysis',
      description: 'Upload contracts and we will tell you what they probably mean. Probably.',
      icon: FileText,
    },
    {
      title: 'Tokens, Not Retainers',
      description: 'Pay for tokens, not hourly rates. Cancel anytime. No awkward breakup calls.',
      icon: Coins,
    },
    {
      title: 'World\'s Best Disclaimers',
      description: 'Every response comes with disclaimers so extensive, you will need a disclaimer for the disclaimer.',
      icon: Shield,
    },
  ];

  const pricingTiers = [
    {
      name: 'Free',
      price: '$0',
      tokens: '500 tokens',
      features: ['500 bonus tokens', 'Basic cheekiness', 'Standard disclaimers', '2 documents max'],
    },
    {
      name: 'Basic',
      price: '$10',
      tokens: '10,000 tokens',
      features: ['10,000 tokens/month', 'Enhanced personality', 'Extra disclaimers', '10 documents max'],
      popular: true,
    },
    {
      name: 'Pro',
      price: '$29',
      tokens: '35,000 tokens',
      features: ['35,000 tokens/month', 'Maximum sass mode', 'Warrior-level disclaimers', 'Unlimited documents'],
    },
  ];

  // CTA Button stamp effect
  useGSAP(() => {
    if (!ctaButtonRef.current) return;

    gsap.set(ctaButtonRef.current, {
      scale: 0.8,
      rotation: -5,
      opacity: 0,
    });

    gsap.to(ctaButtonRef.current, {
      scale: 1,
      rotation: 0,
      opacity: 1,
      duration: 0.6,
      ease: 'elastic.out(1, 0.5)',
      delay: 0.5,
    });
  }, { scope: containerRef });

  return (
    <div ref={containerRef} className="min-h-screen bg-surface-50 font-sans selection:bg-primary-200 selection:text-primary-900 overflow-x-hidden">
      {/* Scroll-Controlled Gavel Hammer */}
      <ScrollGavel
        sections={[
          { id: 'hero', label: '01 // NOT LEGAL ADVICE' },
          { id: 'features', label: '02 // FEATURES' },
          { id: 'pricing', label: '03 // PRICING' },
          { id: 'cta', label: '04 // OBJECTION!' },
        ]}
      />

      {/* ============================================ */}
      {/* HEADER - Sticky with blur */}
      {/* ============================================ */}
      <header className="sticky top-0 z-50 w-full border-b border-surface-200/80 bg-white/80 backdrop-blur-md">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <NotLegalIcon name="gavel" className="w-8 h-8 text-primary-600" />
            <span className="text-xl font-bold text-surface-900">notlegal.chat</span>
          </div>
          <nav className="hidden md:flex items-center space-x-6">
            <Link to="/features" className="text-sm font-medium text-surface-600 hover:text-primary-600 transition-colors">
              Features
            </Link>
            <Link to="/pricing" className="text-sm font-medium text-surface-600 hover:text-primary-600 transition-colors">
              Pricing
            </Link>
            <Link to="/dashboard" className="text-sm font-medium text-surface-600 hover:text-primary-600 transition-colors">
              Dashboard
            </Link>
          </nav>
          <div className="flex items-center space-x-3">
            <Link to="/sign-in">
              <Button variant="ghost" size="sm">Sign In</Button>
            </Link>
            <Link to="/sign-up">
              <Button size="sm">Get Started</Button>
            </Link>
          </div>
        </div>
      </header>

      {/* ============================================ */}
      {/* HERO - Zoom Through Effect */}
      {/* ============================================ */}
      <ZoomThroughHero />

      {/* ============================================ */}
      {/* MARQUEE */}
      {/* ============================================ */}
      <div className="bg-surface-900 py-4 overflow-hidden border-y border-surface-800">
        <div className="flex animate-marquee whitespace-nowrap">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="flex items-center mx-8">
              {['NOT LEGAL ADVICE', 'ENTERTAINMENT ONLY', 'DON\'T SUE US', '0% LAWYERS', '100% SASS', 'ASK YOUR MOM'].map((text, j) => (
                <span key={j} className="text-xl md:text-2xl font-black text-surface-700 flex items-center mx-4">
                  {text}
                  <Zap className="w-5 h-5 text-primary-500 ml-4" />
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* ============================================ */}
      {/* SUBHERO */}
      {/* ============================================ */}
      <SectionWrapper>
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center space-x-2 bg-warning-100 text-warning-800 px-4 py-2 rounded-full mb-8 shadow-sm border border-warning-200">
            <AlertCircle className="w-4 h-4" />
            <span className="text-sm font-medium">This is definitely NOT legal advice.</span>
          </div>

          <TypewriterHeadline />

          <p className="mt-8 text-xl text-surface-600 max-w-2xl mx-auto leading-relaxed">
            Just an AI with an attitude and a law degree from the University of the Internet.
            Cheaper than a lawyer, faster than a paralegal.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/sign-up">
              <Button
                ref={ctaButtonRef}
                size="lg"
                className="h-14 px-10 text-lg shadow-2xl hover:shadow-3xl hover:-translate-y-1 transition-all"
              >
                Start Chatting (Free)
              </Button>
            </Link>
            <a href="/chat?demo=true" onClick={handleDemoClick}>
              <Button variant="secondary" size="lg" className="h-14 px-10 text-lg border-2 border-surface-200 hover:bg-surface-100">
                <Play className="w-5 h-5 mr-2" />
                Try Demo
              </Button>
            </a>
          </div>

          <p className="mt-6 text-sm text-surface-400">
            *Seriously, do not use this in court. The judge will laugh at you.
          </p>
        </div>
      </SectionWrapper>

      {/* ============================================ */}
      {/* GAVEL TRANSITION */}
      {/* ============================================ */}
      <GavelTransition />

      {/* ============================================ */}
      {/* FEATURES - Swipe Text */}
      {/* ============================================ */}
      <SectionWrapper>
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <TextSwipe
              text="Why Risk It?"
              className="text-4xl md:text-5xl font-black text-surface-900 mb-4"
              delay={0}
            />
            <TextSwipe
              text="For Fun, Obviously"
              className="text-xl md:text-2xl text-surface-600"
              delay={0.3}
            />
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {features.map((feature, i) => (
              <FeatureCard key={i} {...feature} />
            ))}
          </div>
        </div>
      </SectionWrapper>

      {/* ============================================ */}
      {/* GAVEL TRANSITION */}
      {/* ============================================ */}
      <GavelTransition />

      {/* ============================================ */}
      {/* PRICING - Document Unroll */}
      {/* ============================================ */}
      <SectionWrapper className="bg-surface-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <TextSwipe
              text="Pricing That Won't"
              className="text-4xl md:text-5xl font-black text-surface-900 mb-2"
            />
            <TextSwipe
              text="Bankrupt You"
              className="text-4xl md:text-5xl font-black text-surface-900"
              delay={0.2}
            />
            <p className="mt-6 text-xl text-surface-600">
              Unlike a real retainer, you can actually afford this.
            </p>
          </div>

          <DocumentUnroll tiers={pricingTiers} />
        </div>
      </SectionWrapper>

      {/* ============================================ */}
      {/* GAVEL TRANSITION */}
      {/* ============================================ */}
      <GavelTransition />

      {/* ============================================ */}
      {/* CTA SECTION */}
      {/* ============================================ */}
      <SectionWrapper dark>
        <div className="max-w-3xl mx-auto text-center">
          <TextSwipe
            text="Ready to Object?"
            className="text-4xl md:text-6xl font-black text-white mb-6"
          />
          <TextSwipe
            text="Join thousands who chose entertainment over accuracy."
            className="text-xl text-primary-200 mb-10"
            delay={0.2}
          />

          <Link to="/sign-up">
            <Button
              size="lg"
              className="h-16 px-12 text-xl bg-white text-primary-900 hover:bg-primary-50 shadow-2xl hover:shadow-3xl hover:scale-105 transition-all"
            >
              Start Your Free Trial
            </Button>
          </Link>
        </div>
      </SectionWrapper>

      {/* ============================================ */}
      {/* FOOTER */}
      {/* ============================================ */}
      <footer className="py-16 px-6 bg-white border-t border-surface-200">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div className="col-span-1 md:col-span-2">
              <div className="flex items-center space-x-2 mb-4">
                <Gavel className="w-6 h-6 text-primary-600" />
                <span className="text-lg font-bold text-surface-900">notlegal.chat</span>
              </div>
              <p className="text-surface-500 max-w-xs">
                Making legal information entertaining since 2024.
                Not affiliated with any actual law firm. Thank goodness.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-surface-900 mb-4">Product</h4>
              <ul className="space-y-3 text-surface-600">
                <li><Link to="/features" className="hover:text-primary-600 transition-colors">Features</Link></li>
                <li><Link to="/pricing" className="hover:text-primary-600 transition-colors">Pricing</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-surface-900 mb-4">Legal (The Boring Kind)</h4>
              <ul className="space-y-3 text-surface-600">
                <li><Link to="/privacy" className="hover:text-primary-600 transition-colors">Privacy Policy</Link></li>
                <li><Link to="/terms" className="hover:text-primary-600 transition-colors">Terms of Service</Link></li>
                <li><Link to="/disclaimer" className="hover:text-primary-600 transition-colors">Disclaimer</Link></li>
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t border-surface-200 text-center text-sm text-surface-400">
            <p>{new Date().getFullYear()} notlegal.chat. All rights reserved. Not a law firm. Definitely not legal advice.</p>
          </div>
        </div>
      </footer>

      {/* ============================================ */}
      {/* CSS FOR MARQUEE ANIMATION */}
      {/* ============================================ */}
      <style>{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-50%); }
        }
        .animate-marquee {
          animation: marquee 20s linear infinite;
        }
        .perspective-1000 {
          perspective: 1000px;
        }
        .transform-gpu {
          transform: translateZ(0);
        }
      `}</style>
    </div>
  );
}

export default LandingPage;
